import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { profilesService } from '@/services/profilesService';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  /** True for any internal staff member (CIWG employee). Canonical predicate: profilesService.isStaff(). Used to gate backoffice access. */
  isStaff: boolean;
  /** True when the user must change their password before continuing (set by admin-created accounts with generated passwords). */
  mustChangePassword: boolean;
  isLoading: boolean;
  isAdminLoading: boolean;
  adminCheckError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);
  const lastCheckedUserId = useRef<string | null>(null);

  const withTimeout = async <T,>(
    promise: Promise<T>,
    ms: number,
    label: string
  ): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutId = window.setTimeout(() => {
        reject(new Error(`${label} timed out after ${ms}ms`));
      }, ms);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  };

  const isCiWebGroupEmail = (email?: string | null) => {
    const e = (email ?? "").toLowerCase().trim();
    return e.endsWith("@ciwebgroup.com");
  };

  const checkAdminRole = async (userId: string): Promise<boolean> => {
    const { data, error } = await supabase.rpc('has_role', {
      _user_id: userId,
      _role: 'admin',
    });

    if (error) {
      throw new Error(`has_role RPC failed: ${error.message}`);
    }

    return !!data;
  };

  const ensureCiWebGroupAdmin = async (email?: string | null) => {
    if (!isCiWebGroupEmail(email)) return;

    try {
      const { error } = await supabase.rpc("ensure_ciwebgroup_admin");
      if (error) {
        console.error("Error ensuring CI Web Group admin role:", error);
      }
    } catch (err) {
      console.error("Error in ensureCiWebGroupAdmin:", err);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const runAdminCheck = async (sessionUser: User) => {
      setAdminCheckError(null);
      let adminStatus = false;
      let adminError: string | null = null;
      let staffError: string | null = null;

      try {
        await withTimeout(ensureCiWebGroupAdmin(sessionUser.email), 5000, 'ensure admin role');
      } catch (provisionErr) {
        console.warn('Admin provisioning skipped (non-fatal):', provisionErr);
      }

      try {
        adminStatus = await withTimeout(checkAdminRole(sessionUser.id), 8000, 'check admin role');
      } catch (err) {
        adminError = err instanceof Error
          ? err.message
          : 'Unable to verify admin permissions. Please try again.';
        console.warn('Admin role check failed (non-fatal for staff access):', err);
      }

      let profile: { user_type?: string | null; must_change_password?: boolean | null } | null = null;
      try {
        profile = await withTimeout(
          profilesService.findByUserId(sessionUser.id, 'user_type, must_change_password'),
          8000,
          'check staff status'
        );
      } catch (err) {
        staffError = err instanceof Error
          ? err.message
          : 'Unable to verify backoffice access. Please try again.';
        console.warn('Staff check failed:', err);
      }

      if (!isMounted) return;

      const staffStatus = adminStatus || profilesService.isStaff(profile);
      setIsAdmin(adminStatus);
      setIsStaff(staffStatus);
      setMustChangePassword(Boolean(profile?.must_change_password));

      if (!staffStatus && (staffError || adminError)) {
        setAdminCheckError(staffError ?? adminError);
      }

      if (isMounted) {
        if (isMounted) setIsAdminLoading(false);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!isMounted) return;

      // Only clear local user state on an explicit SIGNED_OUT or USER_DELETED event.
      // Transient token-refresh failures can fire events with a null session even
      // though the user is still logged in (supabase-js will retry). Clearing
      // state on every null-session event causes random "silent sign-outs" that
      // bounce users back to the homepage mid-session.
      if (!nextSession?.user) {
        if (event === 'SIGNED_OUT' || (event as string) === 'USER_DELETED') {
          setSession(null);
          setUser(null);
          setIsAdmin(false); setIsStaff(false);
          setIsAdminLoading(false);
          setAdminCheckError(null);
          lastCheckedUserId.current = null;
        }
        // For INITIAL_SESSION / TOKEN_REFRESHED with null session: do nothing —
        // initializeAuth() handles the cold-start case, and a failed refresh
        // will eventually emit SIGNED_OUT if truly expired.
        return;
      }

      setSession(nextSession);
      setUser(nextSession.user);

      if (nextSession.user.id !== lastCheckedUserId.current) {
        lastCheckedUserId.current = nextSession.user.id;
        setIsAdminLoading(true);
        setTimeout(() => {
          if (isMounted) runAdminCheck(nextSession.user);
        }, 0);
      }
    });

    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          if (lastCheckedUserId.current !== initialSession.user.id) {
            lastCheckedUserId.current = initialSession.user.id;
            setIsAdminLoading(true);
            await runAdminCheck(initialSession.user);
          }
        } else {
          setIsAdmin(false); setIsStaff(false);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('[AuthContext] Auth initialization failed:', err);
        setIsAdmin(false); setIsStaff(false);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    if (!email.toLowerCase().endsWith('@ciwebgroup.com')) {
      return {
        error: new Error('Registration is restricted to CI Web Group team members only.')
      };
    }

    const redirectUrl = `${window.location.origin}/`;

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        return { error: error as Error | null };
      }

      if (data?.user && data.user.identities?.length === 0) {
        return {
          error: new Error('An account with this email already exists. Please sign in instead.')
        };
      }

      return { error: null };
    } catch (err) {
      console.error('SignUp error:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        return {
          error: new Error('Network error: Unable to connect. Please check your internet connection and try again.')
        };
      }
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false); setIsStaff(false);
    lastCheckedUserId.current = null;
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/backoffice/reset-password?redirect=/backoffice/login`,
      });
      return { error: error as Error | null };
    } catch (err) {
      console.error('Reset password error:', err);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        return {
          error: new Error('Network error: Unable to connect. Please check your internet connection.')
        };
      }
      return { error: err as Error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isStaff,
        mustChangePassword,
        isLoading,
        isAdminLoading,
        adminCheckError,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
